import { Tooltip } from '@buffer-mono/popcorn'

import { useChartSeriesById } from '../ChartSeriesContext'

import type { JSX } from 'react'

// Roughly the number of 12px chars that fit in the category gutter, with a
// tighter cap when the series icon shares the gutter.
const MAX_LABEL_CHARS = 16
const MAX_LABEL_CHARS_WITH_ICON = 13
const ICON_SIZE = 16
const ICON_GAP = 4

interface NetChangeCategoryTickProps {
  /** Geometry + tick data injected by Recharts when used as a YAxis `tick`.
   * `payload.value` is the category value — the series **id** (the axis is keyed
   * by id, since labels aren't unique). */
  x?: number
  y?: number
  payload?: { value?: string | number }
  /** Skip the hover tooltip on truncated names (e.g. static captures with no
   * Popcorn TooltipProvider). */
  disableTooltip?: boolean
}

/**
 * Category-axis tick for a channel/series row. Reads the series from
 * {@link ChartSeriesContext} by id for its label + icon (so it can't drift from
 * the chart), truncates a long label with an ellipsis (full label on hover), and
 * renders the series' icon to the right of the label, nearest the axis.
 */
export function NetChangeCategoryTick({
  x = 0,
  y = 0,
  payload,
  disableTooltip = false,
}: NetChangeCategoryTickProps): JSX.Element {
  const id = String(payload?.value ?? '')
  const series = useChartSeriesById(id)
  const label = series?.label ?? id
  const icon = series?.icon

  const maxChars = icon != null ? MAX_LABEL_CHARS_WITH_ICON : MAX_LABEL_CHARS
  const isTruncated = label.length > maxChars
  const text = isTruncated ? `${label.slice(0, maxChars - 1)}…` : label
  const textEndX = icon != null ? -(ICON_SIZE + ICON_GAP) : 0

  // A clipped label is only fully readable via the tooltip below; make that tick
  // focusable with the full label so keyboard / AT users reach it too — but only
  // when clipped, so untruncated ticks don't add pointless tab stops.
  const focusProps = isTruncated
    ? { tabIndex: 0, 'aria-label': label }
    : undefined

  const tick = (
    <g transform={`translate(${x},${y})`} {...focusProps}>
      <text
        x={textEndX}
        y={0}
        dy={4}
        textAnchor="end"
        fontSize={12}
        // Set fill via CSS (not the SVG attribute) so the token resolves and
        // re-themes in dark mode.
        style={{ fill: 'var(--color-text-subtle)' }}
      >
        {text}
      </text>
      {icon != null && (
        // A translated <g> wrapping the icon's native <svg> (not a
        // <foreignObject>) so it renders live and survives snapshot capture.
        <g transform={`translate(${-ICON_SIZE}, ${-ICON_SIZE / 2})`}>{icon}</g>
      )}
    </g>
  )

  // Only attach the tooltip when the label is actually clipped — and never for a
  // static capture, whose offscreen render root has no TooltipProvider.
  if (!isTruncated || disableTooltip) return tick
  return <Tooltip content={label}>{tick}</Tooltip>
}
